import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRightCircle,
  Download,
  FileText,
  Loader2,
  Save,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EntityCombobox from "@/components/EntityCombobox";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import ProductionOrderPrint, {
  type ProductionOrderPrintData,
} from "@/components/ProductionOrderPrint";
import { formatDate, formatNumber } from "@/lib/format";
import { renderReactToPdf, safePdfFilename } from "@/lib/htmlPdf";
import type { PdfCompany } from "@/lib/pdf";

type PoStatus = "pending" | "in_production" | "completed";

interface CustomerOption {
  id: number;
  name: string;
  phone: string;
}

interface QuoteOption {
  id: number;
  quote_number: string;
  customer_id: number | null;
  customer_name: string;
  telephone: string;
  gauge: string;
  profile: string;
  color: string;
  total_amount: number;
  total_paid: number;
  // Percentage of the quote total that has been received. The picker only ever
  // lists quotes whose payment_percent is strictly greater than 50.
  payment_percent: number;
}

interface PoLine {
  profile: string;
  gauge: string;
  length: number;
  quantity: number;
  unit: string;
  remarks: string;
}

interface StatusLogEntry {
  id: number;
  from_status: string;
  to_status: string;
  changed_by_name: string | null;
  note: string;
  changed_at: string | null;
}

interface PoApi {
  id: number;
  production_order_number: string;
  customer_id: number;
  customer_name: string;
  customer_phone: string;
  quote_id: number | null;
  quotation_ref: string;
  production_date: string | null;
  gauge: string;
  profile: string;
  color: string;
  status: PoStatus;
  remarks: string;
  total_sheets: number;
  total_length: number;
  estimated_material_usage: number;
  items: Array<{
    line_no: number;
    profile: string;
    gauge: string;
    length: number;
    quantity: number;
    unit: string;
    total_quantity: number;
    remarks: string;
  }>;
  status_log: StatusLogEntry[];
  created_by_name?: string | null;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function effectiveLength(length: number) {
  return Number(length) > 0 ? Number(length) : 1;
}

function formatPaymentPercent(percent: number) {
  const n = Number(percent || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function lineTotal(line: PoLine) {
  return (
    Math.round(
      effectiveLength(line.length) * Number(line.quantity || 0) * 100,
    ) / 100
  );
}

function computeTotals(lines: PoLine[]) {
  let totalSheets = 0;
  let totalLength = 0;
  for (const ln of lines) {
    const len = effectiveLength(ln.length);
    const qty = Number(ln.quantity || 0);
    totalSheets += qty;
    totalLength += len * qty;
  }
  return {
    total_sheets: Math.round(totalSheets * 100) / 100,
    total_length: Math.round(totalLength * 100) / 100,
    estimated_material_usage: Math.round(totalLength * 100) / 100,
  };
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "pending":
      return "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30";
    case "in_production":
      return "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30";
    case "completed":
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function statusLabel(status: string): string {
  if (status === "in_production") return "In production";
  if (status === "completed") return "Completed";
  return "Pending";
}

function nextStatus(current: PoStatus): PoStatus | null {
  if (current === "pending") return "in_production";
  if (current === "in_production") return "completed";
  return null;
}

export default function ProductionOrderForm() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const nav = useNavigate();
  const { user, can } = useAuth();
  const canEdit = can("production_orders.write");
  const canStatus = can("production_orders.status") || canEdit;
  const isNew = !id;

  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [quotes, setQuotes] = useState<QuoteOption[]>([]);
  const [gauges, setGauges] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);

  const [orderNumber, setOrderNumber] = useState("");
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [quoteId, setQuoteId] = useState<number | null>(null);
  const [quotationRef, setQuotationRef] = useState("");
  const [productionDate, setProductionDate] = useState(todayIso());
  const [gauge, setGauge] = useState("");
  const [profile, setProfile] = useState("");
  const [color, setColor] = useState("");
  const [remarks, setRemarks] = useState("");
  const [status, setStatus] = useState<PoStatus>("pending");
  const [statusLog, setStatusLog] = useState<StatusLogEntry[]>([]);
  const [lines, setLines] = useState<PoLine[]>([]);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [statusNote, setStatusNote] = useState("");
  const [advancing, setAdvancing] = useState(false);
  const [createdByName, setCreatedByName] = useState<string | null>(null);
  const [company, setCompany] = useState<PdfCompany>({});
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [pdfOrderNumber, setPdfOrderNumber] = useState("");
  const [postSaveNavId, setPostSaveNavId] = useState<number | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [autoOpenConsumed, setAutoOpenConsumed] = useState(false);

  const wantsAutoOpenPdf = useMemo(
    () => new URLSearchParams(search).get("openPdf") === "1",
    [search],
  );

  const readOnly = !canEdit || status === "completed";

  const totals = useMemo(() => computeTotals(lines), [lines]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ settings: Record<string, string> }>(
          "/settings",
        );
        const s = res.settings ?? {};
        setCompany({
          name: s["company.name"] || undefined,
          email: s["company.email"] || undefined,
          phone: s["company.phone"] || undefined,
          address: s["company.address"] || undefined,
          currency_code: s["currency.code"] || undefined,
          currency_symbol: s["currency.symbol"] || undefined,
        });
      } catch {
        // Non-fatal: PDF will still render with default branding.
      }
    })();
  }, []);

  async function loadFormSupport() {
    const res = await api.get<{
      customers: CustomerOption[];
      quotations: QuoteOption[];
      gauges: string[];
      profiles: string[];
      colors: string[];
    }>("/production-orders/form-support");
    setCustomers(res.customers ?? []);
    setQuotes(res.quotations ?? []);
    setGauges(res.gauges ?? []);
    setProfiles(res.profiles ?? []);
    setColors(res.colors ?? []);
  }

  async function loadOrder(orderId: number) {
    setLoading(true);
    try {
      const res = await api.get<{ production_order: PoApi }>(
        `/production-orders/${orderId}`,
      );
      const po = res.production_order;
      setOrderNumber(po.production_order_number);
      setCustomerId(po.customer_id);
      setCustomerName(po.customer_name);
      setCustomerPhone(po.customer_phone);
      setQuoteId(po.quote_id);
      setQuotationRef(po.quotation_ref);
      setProductionDate(po.production_date || todayIso());
      setGauge(po.gauge);
      setProfile(po.profile);
      setColor(po.color);
      setRemarks(po.remarks);
      setStatus(po.status);
      setStatusLog(po.status_log ?? []);
      setCreatedByName(po.created_by_name ?? null);
      const mapped =
        po.items?.length > 0
          ? po.items.map((it) => ({
              profile: it.profile || po.profile,
              gauge: it.gauge || po.gauge,
              length: it.length,
              quantity: it.quantity,
              unit: it.unit || "Pieces",
              remarks: it.remarks || "",
            }))
          : [];
      setLines(mapped);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load order");
      nav("/production-orders");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadFormSupport().catch((err) =>
      toast.error((err as Error).message || "Failed to load form data"),
    );
    if (id) loadOrder(Number(id));
  }, [id]);

  async function importFromQuote(qid: number) {
    setImporting(true);
    try {
      const res = await api.get<{
        import: {
          quote_id: number;
          customer_id: number | null;
          customer_name: string;
          customer_phone: string;
          quotation_ref: string;
          gauge: string;
          profile: string;
          color: string;
          items: PoLine[];
        };
      }>(`/production-orders/quote-import/${qid}`);
      const data = res.import;
      setQuoteId(data.quote_id);
      setQuotationRef(data.quotation_ref);
      if (data.customer_id) setCustomerId(data.customer_id);
      setCustomerName(data.customer_name);
      setCustomerPhone(data.customer_phone);
      setGauge(data.gauge);
      setProfile(data.profile);
      setColor(data.color);
      setLines(
        (data.items ?? []).map((it) => ({
          profile: it.profile || data.profile,
          gauge: it.gauge || data.gauge,
          length: Number(it.length || 1),
          quantity: Number(it.quantity || 0),
          unit: it.unit || "Pieces",
          remarks: it.remarks || "",
        })),
      );
      toast.success("Imported from quotation");
    } catch (err) {
      toast.error((err as Error).message || "Import failed");
    } finally {
      setImporting(false);
    }
  }

  function onQuoteChange(value: string) {
    const n = Number(value) || null;
    if (!n) {
      setQuoteId(null);
      setQuotationRef("");
      return;
    }
    setQuoteId(n);
    const q = quotes.find((x) => x.id === n);
    if (q) setQuotationRef(q.quote_number);
    void importFromQuote(n);
  }

  function mapPoToPrintData(source?: PoApi | null): ProductionOrderPrintData {
    const itemSource =
      source?.items ??
      lines.map((ln, idx) => ({
        line_no: idx + 1,
        profile: ln.profile || profile,
        gauge: ln.gauge || gauge,
        length: ln.length,
        quantity: ln.quantity,
        unit: ln.unit,
        total_quantity: lineTotal(ln),
        remarks: ln.remarks,
      }));
    const computed = source ? null : computeTotals(lines);
    const headerColor = source?.color || color || "";
    return {
      number: source?.production_order_number || orderNumber || "Draft",
      productionDate: source?.production_date || productionDate,
      status: source?.status || status,
      customerName: source?.customer_name || customerName,
      customerPhone: source?.customer_phone || customerPhone,
      quotationRef: source?.quotation_ref || quotationRef || undefined,
      gauge: source?.gauge || gauge || undefined,
      profile: source?.profile || profile || undefined,
      color: source?.color || color || undefined,
      items: itemSource.map((it) => ({
        profile: it.profile || "",
        gauge: it.gauge || "",
        color: headerColor || undefined,
        length: Number(it.length || 0),
        quantity: Number(it.quantity || 0),
        unit: it.unit || "Pieces",
        totalQuantity: Number(it.total_quantity ?? lineTotal({
          profile: it.profile || "",
          gauge: it.gauge || "",
          length: Number(it.length || 0),
          quantity: Number(it.quantity || 0),
          unit: it.unit || "Pieces",
          remarks: it.remarks || "",
        })),
        remarks: it.remarks || undefined,
      })),
      totalSheets: source?.total_sheets ?? computed?.total_sheets ?? 0,
      totalLength: source?.total_length ?? computed?.total_length ?? 0,
      estimatedMaterialUsage:
        source?.estimated_material_usage ??
        computed?.estimated_material_usage ??
        0,
      remarks: source?.remarks || remarks || undefined,
    };
  }

  function resolvePreparedBy(name?: string | null): string {
    const trimmed = name?.trim();
    if (trimmed) return trimmed;
    return (
      user?.name?.trim() ||
      user?.username?.trim() ||
      user?.email?.trim() ||
      "—"
    );
  }

  async function generatePdf(
    printData: ProductionOrderPrintData,
    preparedBy: string,
  ) {
    const printCompany = {
      name: (company.name || "BillForge").toString(),
      address: (company.address || "").toString(),
      phone: (company.phone || "").toString(),
      email: (company.email || "").toString(),
    };
    return renderReactToPdf(
      <ProductionOrderPrint
        order={printData}
        company={printCompany}
        preparedBy={preparedBy}
      />,
      {
        filename: safePdfFilename("PRO", printData.number || "Draft"),
      },
    );
  }

  function openPdfPreview(
    pdf: { url: string; filename: string },
    orderNo: string,
  ) {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(pdf.url);
    setPdfFilename(pdf.filename);
    setPdfOrderNumber(orderNo);
    setPdfModalOpen(true);
  }

  function handlePdfModalOpenChange(open: boolean) {
    if (!open) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPdfFilename("");
      setPdfOrderNumber("");
      setPdfModalOpen(false);
      if (postSaveNavId != null) {
        nav(`/production-orders/${postSaveNavId}`);
        setPostSaveNavId(null);
      }
    } else {
      setPdfModalOpen(true);
    }
  }

  async function handleDownloadPdfFromForm() {
    try {
      const pdf = await generatePdf(
        mapPoToPrintData(),
        resolvePreparedBy(createdByName),
      );
      const a = document.createElement("a");
      a.href = pdf.url;
      a.download = pdf.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(pdf.url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    }
  }

  async function handlePreviewPdfFromForm() {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    try {
      const printData = mapPoToPrintData();
      const pdf = await generatePdf(printData, resolvePreparedBy(createdByName));
      openPdfPreview(pdf, printData.number || "Draft");
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  useEffect(() => {
    if (!wantsAutoOpenPdf || autoOpenConsumed) return;
    if (!id) return;
    if (!orderNumber) return;
    setAutoOpenConsumed(true);
    void handlePreviewPdfFromForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsAutoOpenPdf, autoOpenConsumed, id, orderNumber]);

  async function save() {
    if (!quoteId) {
      toast.error(
        "Select a quotation — production order details are imported from it.",
      );
      return;
    }
    if (!customerId) {
      toast.error("Please select a customer");
      return;
    }
    if (!lines.length) {
      toast.error("No items imported from the quotation yet.");
      return;
    }
    const payload = {
      customer_id: customerId,
      customer_name: customerName,
      customer_phone: customerPhone,
      quote_id: quoteId,
      quotation_ref: quotationRef || null,
      production_date: productionDate,
      gauge: gauge || null,
      profile: profile || null,
      color: color || null,
      remarks: remarks || null,
      items: lines.map((ln) => ({
        profile: ln.profile || profile || null,
        gauge: ln.gauge || gauge || null,
        length: ln.length,
        quantity: ln.quantity,
        unit: ln.unit,
        remarks: ln.remarks || null,
      })),
    };
    setSaving(true);
    try {
      let saved: PoApi | null = null;
      if (isNew) {
        const res = await api.post<{ production_order: PoApi }>(
          "/production-orders",
          payload,
        );
        saved = res.production_order ?? null;
        toast.success("Production order created");
      } else {
        const res = await api.put<{ production_order: PoApi }>(
          `/production-orders/${id}`,
          payload,
        );
        saved = res.production_order ?? null;
        toast.success("Production order saved");
      }
      if (saved) {
        setOrderNumber(saved.production_order_number);
        setCreatedByName(saved.created_by_name ?? null);
        if (saved.id != null) setPostSaveNavId(saved.id);
      }
      try {
        const printData = mapPoToPrintData(saved);
        const pdf = await generatePdf(
          printData,
          resolvePreparedBy(saved?.created_by_name ?? createdByName),
        );
        openPdfPreview(
          pdf,
          saved?.production_order_number || orderNumber || "Draft",
        );
      } catch (pdfErr) {
        toast.error((pdfErr as Error).message || "Could not render PDF");
        if (saved?.id != null) nav(`/production-orders/${saved.id}`);
      }
    } catch (err) {
      toast.error((err as Error).message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function advanceStatus() {
    const target = nextStatus(status);
    if (!target || !id) return;
    setAdvancing(true);
    try {
      const res = await api.patch<{ production_order: PoApi }>(
        `/production-orders/${id}/status`,
        { status: target, note: statusNote.trim() || undefined },
      );
      setStatus(res.production_order.status);
      setStatusLog(res.production_order.status_log ?? []);
      setStatusNote("");
      toast.success(`Status: ${statusLabel(target)}`);
    } catch (err) {
      toast.error((err as Error).message || "Status update failed");
    } finally {
      setAdvancing(false);
    }
  }

  if (loading) {
    return (
      <div className="py-16 text-center text-muted-foreground">Loading...</div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1200px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav("/production-orders")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {isNew ? "New Production Order" : orderNumber}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isNew
                ? "Create a factory production order"
                : "Roofing sheet production details"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew ? (
            <Button
              type="button"
              variant="outline"
              onClick={handlePreviewPdfFromForm}
              disabled={generatingPdf}
              title="Preview PDF"
            >
              <FileText className="h-4 w-4 mr-2" />
              {generatingPdf ? "Preparing..." : "Preview PDF"}
            </Button>
          ) : null}
          {!isNew ? (
            <Button
              variant="outline"
              onClick={handleDownloadPdfFromForm}
              title="Download PDF of current production order"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          ) : null}
          {!readOnly ? (
            <Button
              onClick={save}
              disabled={saving || importing}
              className="rounded-xl"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          ) : null}
        </div>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Order information</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>PO Number</Label>
            <Input
              value={isNew ? "(auto-generated on save)" : orderNumber}
              readOnly
              className="font-mono bg-muted/50"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Production date</Label>
            <Input
              type="date"
              value={productionDate}
              onChange={(e) => setProductionDate(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>
              Quotation <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              <EntityCombobox
                aria-label="Quotation"
                className="flex-1 min-w-[200px]"
                value={quotationRef}
                disabled={readOnly || importing}
                placeholder="Select a quotation to import from"
                options={quotes.map((q) => ({
                  id: q.id,
                  name: q.quote_number,
                  secondary: `${q.customer_name} · ${formatPaymentPercent(
                    q.payment_percent,
                  )}% paid`,
                }))}
                emptyText="No eligible quotations."
                onSelect={(opt) => onQuoteChange(String(opt.id))}
                onClear={() => onQuoteChange("")}
              />
              {importing ? (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing…
                </span>
              ) : null}
            </div>
            {quotes.length === 0 && !readOnly ? (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                No eligible quotations. A quotation must be non-draft, more than
                50% paid, and not already used for a waybill.
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              Only quotations that are more than 50% paid and not already used
              for a waybill are listed. Production order details (items, gauge,
              profile, color, customer) are imported from the selected
              quotation.
            </p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Customer</Label>
            <Input
              value={customerName || "—"}
              readOnly
              className="bg-muted/50"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input
              value={customerPhone || "—"}
              readOnly
              className="bg-muted/50"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Gauge</Label>
            <Input value={gauge || "—"} readOnly className="bg-muted/50" />
          </div>
          <div className="space-y-1.5">
            <Label>Profile</Label>
            <Input value={profile || "—"} readOnly className="bg-muted/50" />
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <Input value={color || "—"} readOnly className="bg-muted/50" />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Production items</CardTitle>
          <p className="text-sm text-muted-foreground">
            Imported from the selected quotation (read-only).
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Profile</TableHead>
                <TableHead>Gauge</TableHead>
                <TableHead className="text-right">Length</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Total Qty</TableHead>
                <TableHead>Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center text-muted-foreground py-8"
                  >
                    {quoteId
                      ? "No items found on the selected quotation."
                      : "Select a quotation above to import its items."}
                  </TableCell>
                </TableRow>
              ) : (
                lines.map((ln, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">
                      {ln.profile || "—"}
                    </TableCell>
                    <TableCell>{ln.gauge || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(effectiveLength(ln.length))}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(Number(ln.quantity || 0))}
                    </TableCell>
                    <TableCell>{ln.unit || "Pieces"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatNumber(lineTotal(ln))}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {ln.remarks || "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="shadow-card border-primary/20">
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-sm text-muted-foreground">Total sheets</p>
            <p className="text-2xl font-bold tabular-nums">
              {formatNumber(totals.total_sheets)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total length (m)</p>
            <p className="text-2xl font-bold tabular-nums">
              {formatNumber(totals.total_length)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">
              Estimated material usage (m)
            </p>
            <p className="text-2xl font-bold tabular-nums">
              {formatNumber(totals.estimated_material_usage)}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Remarks</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            placeholder="Order-level remarks (optional)"
            disabled={readOnly}
          />
        </CardContent>
      </Card>

      {!isNew ? (
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              Factory status
              <Badge variant="outline" className={statusBadgeClass(status)}>
                {statusLabel(status)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {canStatus && nextStatus(status) ? (
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px] space-y-1.5">
                  <Label>Note (optional)</Label>
                  <Input
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder="Status change note"
                  />
                </div>
                <Button onClick={advanceStatus} disabled={advancing}>
                  <ArrowRightCircle className="h-4 w-4 mr-2" />
                  {advancing
                    ? "Updating..."
                    : status === "pending"
                      ? "Start production"
                      : "Mark completed"}
                </Button>
              </div>
            ) : null}

            {statusLog.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statusLog.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm">
                        {entry.changed_at
                          ? formatDate(entry.changed_at.slice(0, 10))
                          : "—"}
                      </TableCell>
                      <TableCell>{statusLabel(entry.from_status)}</TableCell>
                      <TableCell>{statusLabel(entry.to_status)}</TableCell>
                      <TableCell>{entry.changed_by_name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {entry.note || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">
                No status changes recorded yet.
              </p>
            )}
          </CardContent>
        </Card>
      ) : null}

      <PdfPreviewModal
        open={pdfModalOpen}
        onOpenChange={handlePdfModalOpenChange}
        pdfUrl={pdfPreviewUrl}
        filename={pdfFilename}
        title="Production Order PDF"
        documentNumber={pdfOrderNumber}
      />
    </div>
  );
}
